import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionTiers, getCurrentSubscription, createCheckout } from '../lib/payment.api';
import { TierInfo, Subscription, SubscriptionTier } from '../types/payment';
import { useNotificationStore } from '../stores/notification.store';

export default function PaymentPlans() {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [processingTier, setProcessingTier] = useState<SubscriptionTier | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [tiersData, subscription] = await Promise.all([
        getSubscriptionTiers(),
        getCurrentSubscription().catch(() => null),
      ]);
      setTiers(tiersData.tiers);
      setStripeConfigured(tiersData.stripeConfigured);
      setCurrentSubscription(subscription);
    } catch (error) {
      console.error('Error loading payment plans:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load payment plans',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (tier: SubscriptionTier) => {
    if (tier === SubscriptionTier.FREE) {
      addNotification({
        type: 'info',
        title: 'Free Tier',
        message: 'You are already on the free tier',
      });
      return;
    }

    if (!stripeConfigured) {
      addNotification({
        type: 'warning',
        title: 'Payments Unavailable',
        message: 'Payment processing is not configured. Please contact support.',
      });
      return;
    }

    try {
      setProcessingTier(tier);
      const { url } = await createCheckout(tier);
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      addNotification({
        type: 'error',
        title: 'Checkout Error',
        message: 'Failed to create checkout session',
      });
      setProcessingTier(null);
    }
  };

  const getTierColor = (tier: SubscriptionTier) => {
    switch (tier) {
      case SubscriptionTier.FREE:
        return {
          border: 'border-cyber-text-muted',
          glow: '',
          text: 'text-cyber-text-muted',
          button: 'border-cyber-text-muted text-cyber-text-muted',
        };
      case SubscriptionTier.PRO:
        return {
          border: 'border-cyber-neon-blue',
          glow: 'shadow-neon-blue',
          text: 'text-cyber-neon-blue',
          button: 'border-cyber-neon-blue text-cyber-neon-blue hover:bg-cyber-neon-blue/10',
        };
      case SubscriptionTier.ENTERPRISE:
        return {
          border: 'border-cyber-neon-purple',
          glow: 'shadow-neon-purple',
          text: 'text-cyber-neon-purple',
          button: 'border-cyber-neon-purple text-cyber-neon-purple hover:bg-cyber-neon-purple/10',
        };
      default:
        return {
          border: 'border-cyber-border',
          glow: '',
          text: 'text-cyber-text-primary',
          button: 'border-cyber-neon-blue text-cyber-neon-blue',
        };
    }
  };

  const formatPrice = (price: number) => {
    return (price / 100).toFixed(2);
  };

  const isCurrentTier = (tier: SubscriptionTier) => {
    return currentSubscription?.tier === tier;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cyber-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="cyber-spinner mx-auto mb-4"></div>
          <p className="text-sm text-cyber-text-secondary font-mono animate-pulse">
            &gt; LOADING PAYMENT PLANS...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cyber-bg-primary">
      {/* Cyberpunk grid background */}
      <div className="fixed inset-0 bg-cyber-grid opacity-20 pointer-events-none"></div>

      {/* Header */}
      <header className="bg-cyber-bg-secondary border-b border-cyber-border relative overflow-hidden shadow-neon-blue">
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-cyber-neon-blue to-transparent opacity-50 animate-pulse"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-cyber-text-secondary hover:text-cyber-neon-blue mb-4 flex items-center font-mono transition-colors group"
          >
            <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>&gt; BACK TO DASHBOARD</span>
          </button>

          <div className="text-center">
            <h1 className="text-4xl font-display font-bold neon-text uppercase tracking-wider mb-3">
              SUBSCRIPTION PLANS
            </h1>
            <p className="text-cyber-text-secondary font-mono text-sm">
              &gt; Choose the perfect plan for your needs
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {!stripeConfigured && (
          <div className="mb-8 cyber-card border-2 border-cyber-neon-green bg-cyber-neon-green/10">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-display font-bold text-cyber-neon-green uppercase tracking-wider mb-2">
                  Demo Mode
                </h3>
                <p className="text-cyber-text-secondary text-sm font-mono">
                  Payments are currently in demo mode. Stripe is not configured. Contact the administrator to enable live payments.
                </p>
              </div>
            </div>
          </div>
        )}

        {currentSubscription && (
          <div className="mb-8 cyber-card border-2 border-cyber-neon-blue bg-cyber-neon-blue/10">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <span className="text-2xl">ℹ️</span>
                <div>
                  <h3 className="font-display font-bold text-cyber-neon-blue uppercase tracking-wider mb-2">
                    Current Plan
                  </h3>
                  <p className="text-cyber-text-secondary text-sm font-mono">
                    You are currently on the <span className="text-cyber-neon-blue font-bold">{currentSubscription.tier.toUpperCase()}</span> tier.
                    {currentSubscription.cancelAtPeriodEnd && (
                      <span className="text-cyber-error ml-2">
                        (Cancels on {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/settings/billing')}
                className="cyber-button text-xs py-2 px-4"
              >
                Manage Billing
              </button>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier) => {
            const colors = getTierColor(tier.tier);
            const isCurrent = isCurrentTier(tier.tier);
            const isProcessing = processingTier === tier.tier;

            return (
              <div
                key={tier.tier}
                className={`cyber-card border-2 ${colors.border} ${colors.glow} relative overflow-hidden ${
                  tier.tier === SubscriptionTier.PRO ? 'transform scale-105' : ''
                }`}
              >
                {tier.tier === SubscriptionTier.PRO && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-cyber-neon-blue to-cyber-neon-purple py-2 text-center">
                    <span className="text-xs font-display font-bold text-white uppercase tracking-wider">
                      ⭐ Most Popular
                    </span>
                  </div>
                )}

                <div className={tier.tier === SubscriptionTier.PRO ? 'mt-8' : ''}>
                  {/* Tier Name */}
                  <div className="text-center mb-6">
                    <h2 className={`text-2xl font-display font-bold uppercase tracking-wider ${colors.text} mb-2`}>
                      {tier.name}
                    </h2>
                    <div className="flex items-baseline justify-center space-x-1">
                      <span className="text-4xl font-display font-bold text-cyber-text-primary">
                        ${formatPrice(tier.price)}
                      </span>
                      <span className="text-cyber-text-secondary font-mono text-sm">
                        /{tier.interval}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center space-x-3">
                      <span className="text-cyber-success text-lg">✓</span>
                      <span className="text-cyber-text-secondary text-sm font-mono">
                        {tier.features.maxMundus === -1 ? 'Unlimited' : tier.features.maxMundus} Mundus
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-cyber-success text-lg">✓</span>
                      <span className="text-cyber-text-secondary text-sm font-mono">
                        {tier.features.maxAgents === -1 ? 'Unlimited' : tier.features.maxAgents} AI Agents
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-cyber-success text-lg">✓</span>
                      <span className="text-cyber-text-secondary text-sm font-mono">
                        {tier.features.maxMetrics === -1 ? 'Unlimited' : tier.features.maxMetrics} Metrics
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={tier.features.aiAnalysis ? 'text-cyber-success' : 'text-cyber-text-muted'}>
                        {tier.features.aiAnalysis ? '✓' : '✕'}
                      </span>
                      <span className={`text-sm font-mono ${tier.features.aiAnalysis ? 'text-cyber-text-secondary' : 'text-cyber-text-muted'}`}>
                        AI Analysis
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={tier.features.emailReports ? 'text-cyber-success' : 'text-cyber-text-muted'}>
                        {tier.features.emailReports ? '✓' : '✕'}
                      </span>
                      <span className={`text-sm font-mono ${tier.features.emailReports ? 'text-cyber-text-secondary' : 'text-cyber-text-muted'}`}>
                        Email Reports
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={tier.features.apiAccess ? 'text-cyber-success' : 'text-cyber-text-muted'}>
                        {tier.features.apiAccess ? '✓' : '✕'}
                      </span>
                      <span className={`text-sm font-mono ${tier.features.apiAccess ? 'text-cyber-text-secondary' : 'text-cyber-text-muted'}`}>
                        API Access
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={tier.features.prioritySupport ? 'text-cyber-success' : 'text-cyber-text-muted'}>
                        {tier.features.prioritySupport ? '✓' : '✕'}
                      </span>
                      <span className={`text-sm font-mono ${tier.features.prioritySupport ? 'text-cyber-text-secondary' : 'text-cyber-text-muted'}`}>
                        Priority Support
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={tier.features.customBranding ? 'text-cyber-success' : 'text-cyber-text-muted'}>
                        {tier.features.customBranding ? '✓' : '✕'}
                      </span>
                      <span className={`text-sm font-mono ${tier.features.customBranding ? 'text-cyber-text-secondary' : 'text-cyber-text-muted'}`}>
                        Custom Branding
                      </span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSubscribe(tier.tier)}
                    disabled={isCurrent || isProcessing || tier.tier === SubscriptionTier.FREE}
                    className={`w-full cyber-button ${colors.button} disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2`}
                  >
                    {isProcessing ? (
                      <>
                        <div className="cyber-spinner w-4 h-4 border-2"></div>
                        <span>Processing...</span>
                      </>
                    ) : isCurrent ? (
                      <span>Current Plan</span>
                    ) : tier.tier === SubscriptionTier.FREE ? (
                      <span>Get Started</span>
                    ) : (
                      <span>Upgrade Now</span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-display font-bold text-cyber-text-primary uppercase tracking-wider text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="cyber-card">
              <h3 className="font-display font-bold text-cyber-neon-blue uppercase tracking-wider mb-2 text-sm">
                Can I change plans anytime?
              </h3>
              <p className="text-cyber-text-secondary text-sm font-mono">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated automatically.
              </p>
            </div>
            <div className="cyber-card">
              <h3 className="font-display font-bold text-cyber-neon-blue uppercase tracking-wider mb-2 text-sm">
                What payment methods do you accept?
              </h3>
              <p className="text-cyber-text-secondary text-sm font-mono">
                We accept all major credit cards, debit cards, and digital wallets through Stripe.
              </p>
            </div>
            <div className="cyber-card">
              <h3 className="font-display font-bold text-cyber-neon-blue uppercase tracking-wider mb-2 text-sm">
                Can I cancel anytime?
              </h3>
              <p className="text-cyber-text-secondary text-sm font-mono">
                Yes, you can cancel your subscription at any time. You'll retain access until the end of your billing period.
              </p>
            </div>
            <div className="cyber-card">
              <h3 className="font-display font-bold text-cyber-neon-blue uppercase tracking-wider mb-2 text-sm">
                Is there a free trial?
              </h3>
              <p className="text-cyber-text-secondary text-sm font-mono">
                The Free tier is available forever with no credit card required. Upgrade when you're ready for more features.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
